import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Popover, Tooltip, Typography } from '@mui/material';
import { ReactComponent as CriticalAssetColorIcon } from '../../assets/16x16/critical-asset-tag-color.svg';
import { ReactComponent as CloseIcon } from '../../assets/close-2.svg';
import { ReactComponent as FolderIcon } from '../../assets/filter-icons/folder.svg';
import { ReactComponent as HashIcon } from '../../assets/filter-icons/hash.svg';
import { ReactComponent as ResourceTypeIcon } from '../../assets/filter-icons/resource-type.svg';
import { ReactComponent as SchemaIcon } from '../../assets/filter-icons/schema.svg';
import { ReactComponent as SeverityIcon } from '../../assets/filter-icons/severity.svg';
import { ReactComponent as TableIcon } from '../../assets/filter-icons/table.svg';
import { ReactComponent as TestIcon } from '../../assets/filter-icons/test.svg';
import { ReactComponent as TreeIcon } from '../../assets/filter-icons/tree.svg';
import { ReactComponent as UserIcon } from '../../assets/filter-icons/user.svg';
import { ReactComponent as ShieldIcon } from '../../assets/20x20/shield.svg';
import { ReactComponent as LineageIcon } from '../../assets/lineage-nav-button-2.svg';
import { UNSET_FILTER_VALUE, UNSET_FILTER_VALUE_LABEL } from '../../constants';
import { Operator } from '../../hooks/useUrlFilters';
import { DropDownOption } from '../DropDown';
import { FilterOption, OnSubmitEventVal } from '../FilterSelector';
import { Flex, FlexCenter } from '../Flex';
import IconButton from '../IconButton';
import OverflowTooltip from '../OverflowTooltip';
import DateRangeTableFilter from '../Table/TableFilters/DateRangeTableFilter';
import { ColumnType } from '../Table/types';
import { componentToken } from '../../style/utils';
import FilterChipModal from './FilterChipModal';
import OptionsMenu from './OptionsMenu';

const filterData: Record<string, { label: string; icon?: JSX.Element }> = {
  schema: { label: 'Schema', icon: <SchemaIcon /> },
  table: { label: 'Table', icon: <TableIcon /> },
  tablename: { label: 'Table', icon: <TableIcon /> },
  model: { label: 'Model', icon: <TableIcon /> },
  models: { label: 'Model', icon: <TableIcon /> },
  'table name': { label: 'Table name', icon: <TableIcon /> },
  'test name': { label: 'Test name', icon: <TestIcon /> },
  'test type': { label: 'Test type', icon: <TestIcon /> },
  type: { label: 'Type' },
  severity: { label: 'Severity', icon: <SeverityIcon /> },
  'created by': { label: 'Created by', icon: <UserIcon /> },
  owners: { label: 'Owner', icon: <UserIcon /> },
  owner: { label: 'Owner', icon: <UserIcon /> },
  subscribers: { label: 'Subscriber', icon: <UserIcon /> },
  subscriber: { label: 'Subscriber', icon: <UserIcon /> },
  status: { label: 'Status', icon: <SeverityIcon /> },
  statuses: { label: 'Status', icon: <SeverityIcon /> },
  tags: { label: 'Tag', icon: <HashIcon /> },
  tag: { label: 'Tag', icon: <HashIcon /> },
  resource_types: { label: 'Resource type', icon: <ResourceTypeIcon /> },
  is_critical: {
    label: 'Critical',
    icon: <CriticalAssetColorIcon width={20} />,
  },
  iscritical: {
    label: 'Critical',
    icon: <CriticalAssetColorIcon width={20} />,
  },
  critical_asset: {
    label: 'Critical Asset',
    icon: <CriticalAssetColorIcon width={20} />,
  },
  criticalasset: {
    label: 'Critical Asset',
    icon: <CriticalAssetColorIcon width={20} />,
  },
  job: { label: 'Job', icon: <TestIcon /> },
  test: { label: 'Test', icon: <TestIcon /> },
  databasename: { label: 'Database name', icon: <TableIcon /> },
  materialization: { label: 'Materialization', icon: <TreeIcon /> },
  nodetype: { label: 'dbt node type', icon: <ResourceTypeIcon /> },
  binodetype: { label: 'BI node type', icon: <ResourceTypeIcon /> },
  path: { label: 'Path', icon: <FolderIcon /> },
  schemaname: { label: 'Schema name', icon: <SchemaIcon /> },
  assignee: { label: 'Assignee', icon: <UserIcon /> },
  monitor_count: { label: 'Monitor Count', icon: <HashIcon /> },
  testnames: { label: 'Test names', icon: <TestIcon /> },
  lineage: { label: 'Lineage', icon: <LineageIcon width={16} height={16} /> },
  'coverage.percentageamount': {
    label: 'Coverage',
    icon: <ShieldIcon width={16} height={16} />,
  },
};

const operatorMap: Record<Operator, string> = {
  contains: 'contains',
  equals: 'is',
  greaterThan: 'greater than',
  greaterThanOrEqual: 'at least',
  lessThan: 'less than',
  lessThanOrEqual: 'at most',
  notContains: 'does not contain',
  notEquals: 'is not',
  upstreamFrom: 'upstream from',
  downstreamFrom: 'downstream from',
};

const GrayText = styled(Typography).attrs({ component: 'span' })`
  color: ${componentToken('filterChip.grayText.color')};
  font-size: ${componentToken('filterChip.grayText.fontSize')};
  white-space: nowrap;
  display: inline;
`;

const Wrapper = styled(FlexCenter)<{
  $clickable: boolean;
  $hasDeleteButton: boolean;
}>`
  justify-content: space-between;
  background-color: ${componentToken('filterChip.wrapper.backgroundColor')};
  border: 1px solid ${componentToken('filterChip.wrapper.border')};
  border-radius: ${componentToken('filterChip.wrapper.borderRadius')};
  padding: ${({ $hasDeleteButton }) =>
    $hasDeleteButton
      ? componentToken('filterChip.wrapper.deletablePadding')
      : componentToken('filterChip.wrapper.padding')};
  height: 32px;
  gap: ${componentToken('filterChip.wrapper.gap')};
  max-width: 350px;
  overflow: hidden;
  ${({ $clickable }) => $clickable && 'cursor: pointer'};
`;

const CloseIconStyled = styled(CloseIcon)`
  width: 13px;
  height: 13px;
`;

const CloseButtonWrapper = styled(FlexCenter)`
  height: 100%;
  border-left: 1px solid
    ${componentToken('filterChip.closeButtonWrapper.borderLeft')};
  padding-left: ${componentToken('filterChip.closeButtonWrapper.paddingLeft')};
`;

const IconButtonStyled = styled(IconButton)`
  padding: ${componentToken('filterChip.iconButton.padding')};
`;

const PopoverStyled = styled(Popover)`
  .MuiPaper-root {
    padding: 0;
    width: 300px;
  }
`;

const FilterValueText = styled(Typography)`
  font-size: ${componentToken('filterChip.filterValueText.fontSize')};
  color: inherit;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const FlexGap = styled(Flex)`
  gap: ${componentToken('filterChip.flexGap.gap')};
  overflow: hidden;
`;

const TooltipContentWrapper = styled(FlexGap)`
  max-height: 480px;
  flex-wrap: wrap;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${componentToken('filterChip.tooltipContentWrapper.padding')};
  align-items: center;
`;

const FilterValueSection = styled(Flex)`
  align-items: center;
  gap: ${componentToken('filterChip.filterValueSection.gap')};
  max-width: 190px;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const FilterValues = ({ values = [] }: { values: (string | undefined)[] }) => {
  const content = values.map((value, index) => (
    <React.Fragment key={`${value}-${index}`}>
      {index !== 0 && <GrayText> or </GrayText>}
      {value}
    </React.Fragment>
  ));

  return (
    <Tooltip title={<TooltipContentWrapper>{content}</TooltipContentWrapper>}>
      <Flex sx={{ width: '100%' }}>
        <FlexGap>
          <FilterValueText>{content}</FilterValueText>
        </FlexGap>
      </Flex>
    </Tooltip>
  );
};

type Props = {
  filterOptions?: FilterOption[];
  property: string;
  propertyLabel?: string;
  emptyValueLabel?: string;
  type?: ColumnType;
  relationship: Operator;
  index?: number;
  value: string[];
  onDeleteClick?: () => void;
  onSubmit?: (val: OnSubmitEventVal) => void;
};

const FilterChip = ({
  property,
  propertyLabel,
  emptyValueLabel = UNSET_FILTER_VALUE_LABEL,
  relationship,
  value,
  index,
  type,
  onDeleteClick,
  filterOptions = [],
  onSubmit,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const selectedFilterOption = useMemo(
    () => filterOptions.find((o) => o.property === property),
    [filterOptions, property],
  );

  const selectedFilterOptions = selectedFilterOption?.options || [];
  const showSearch = selectedFilterOption?.showSearch ?? true;
  const isMultiselect = selectedFilterOption?.multiselect ?? true;

  const onOpenModal = () => {
    setModalIsOpen(true);
  };

  const onCloseModal = () => {
    setModalIsOpen(false);
  };

  const onItemClick = (newVal: string) => {
    let filteredNewVal: string[] = [];

    if (!isMultiselect) {
      // Single-select: replace value
      filteredNewVal = value.includes(newVal) ? [] : [newVal];
    } else if (newVal === 'all') {
      if (!value.includes('all')) {
        filteredNewVal = [...selectedFilterOptions.map((o) => o.value), 'all'];
      }
    } else if (value.includes(newVal)) {
      filteredNewVal = value.filter((v) => v !== newVal);
    } else {
      filteredNewVal = [...value, newVal];
    }

    onSubmit?.({
      index,
      property,
      operator: relationship,
      prevValue: value,
      value: filteredNewVal,
    });
  };

  const onSelectAllFilteredOptions = (options: DropDownOption[]) => {
    const filteredValues = options.map((o) => o.value);
    const newValues = [...new Set([...value, ...filteredValues])];

    onSubmit?.({
      index,
      property,
      operator: relationship,
      prevValue: value,
      value: newValues,
    });
  };

  const onDeselectAllFilteredOptions = (options: DropDownOption[]) => {
    const filteredValues = new Set(options.map((o) => o.value));
    const newValues = value.filter(
      (v) => !filteredValues.has(v) && v !== 'all',
    );

    onSubmit?.({
      index,
      property,
      operator: relationship,
      prevValue: value,
      value: newValues,
    });
  };

  const isContainsOperator =
    relationship === 'contains' || relationship === 'notContains';

  const isDateRange = type === ColumnType.Date;

  const indeterminate =
    value.includes('all') &&
    !!selectedFilterOptions.find(
      (s) => !value.filter((v) => v !== 'all').includes(s.value),
    );

  return (
    <Wrapper
      key={`${property}-${relationship}`}
      $hasDeleteButton={!!onDeleteClick}
      $clickable={!!filterOptions.length}
    >
      <Flex
        sx={{ alignItems: 'center', gap: 1, width: '100%', overflow: 'hidden' }}
        ref={optionsContainerRef}
        onClick={(e: any) => {
          if (!isContainsOperator) {
            setAnchorEl(e.currentTarget);
          } else {
            onOpenModal();
          }
        }}
      >
        <Flex sx={{ alignItems: 'center', gap: 1 }}>
          {filterData[property?.toLowerCase() || property]?.icon}
          <Typography
            sx={{ fontSize: componentToken('filterChip.root.fontSize') }}
            noWrap
          >
            {propertyLabel ||
              filterData[property.toLowerCase()]?.label ||
              property}
          </Typography>
        </Flex>
        <GrayText>{operatorMap[relationship]}</GrayText>
        <FilterValueSection>
          {isDateRange ? (
            <OverflowTooltip
              sx={{ fontSize: componentToken('filterChip.root.fontSize') }}
              key={`${property}-${relationship}`}
            >
              {value[2] === 'true'
                ? emptyValueLabel
                : `From ${value[0] || '-'} To ${value[1] || 'Now'}`}
            </OverflowTooltip>
          ) : (
            <FilterValues
              values={value
                .filter((v) => v !== 'all')
                .map((v) => {
                  if (v === UNSET_FILTER_VALUE) {
                    return UNSET_FILTER_VALUE_LABEL;
                  }
                  // Find the corresponding label for this value
                  const option = selectedFilterOptions.find(
                    (o) => o.value?.toLowerCase() === v.toLowerCase(),
                  );
                  return option?.label || v || emptyValueLabel;
                })}
            />
          )}
        </FilterValueSection>
      </Flex>
      {onSubmit && (
        <>
          {!isContainsOperator &&
            (isDateRange ? (
              <PopoverStyled
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                  vertical: 35,
                  horizontal: 'left',
                }}
                slotProps={{
                  paper: {
                    style: {
                      maxHeight: '400px',
                    },
                  },
                }}
              >
                <DateRangeTableFilter
                  emptyValueLabel={emptyValueLabel}
                  range={value}
                  onChange={(newValue) =>
                    onSubmit?.({
                      index,
                      property,
                      operator: relationship,
                      prevValue: value,
                      value: (newValue || []) as string[],
                    })
                  }
                />
              </PopoverStyled>
            ) : (
              <OptionsMenu
                anchorElement={anchorEl}
                setAnchorElement={setAnchorEl}
                multiple={isMultiselect}
                showSearch={showSearch}
                showSelectAll={isMultiselect && showSearch}
                indeterminate={indeterminate}
                filteredOptions={selectedFilterOptions}
                handleItemClick={onItemClick}
                onSelectAllFilteredOptions={onSelectAllFilteredOptions}
                onDeselectAllFilteredOptions={onDeselectAllFilteredOptions}
                selectedItems={value}
                showSelectedSection
              />
            ))}

          <FilterChipModal
            open={modalIsOpen}
            value={value}
            handleItemClick={(newVal) => {
              onSubmit?.({
                property,
                operator: relationship,
                prevValue: value,
                value: [newVal],
                index,
              });
            }}
            onCloseModal={onCloseModal}
            propertyLabel={
              propertyLabel ||
              filterData[property.toLowerCase()]?.label ||
              property
            }
          />
        </>
      )}
      {!!onDeleteClick && (
        <CloseButtonWrapper>
          <IconButtonStyled onClick={onDeleteClick}>
            <CloseIconStyled />
          </IconButtonStyled>
        </CloseButtonWrapper>
      )}
    </Wrapper>
  );
};

export default FilterChip;
